import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, Undo, Redo, Eraser } from "lucide-react";
import { sanitizeHtml } from "@/lib/sanitize";

/**
 * Lightweight WYSIWYG editor based on contentEditable.
 * Stores HTML. Exposes value (HTML string) + onChange.
 * Toolbar: bold, italic, underline, h2, ul, ol, link, undo/redo, clear formatting.
 */
export default function RichTextEditor({ value, onChange, placeholder, testid }) {
  const editorRef = useRef(null);
  const lastValueRef = useRef(value || "");
  const [isFocused, setIsFocused] = useState(false);

  // Sync external value into the editor only when it differs (avoid resetting caret while typing)
  useEffect(() => {
    if (!editorRef.current) return;
    const incoming = sanitizeHtml(value || "");
    if (incoming !== lastValueRef.current && incoming !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = incoming;
      lastValueRef.current = incoming;
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const emit = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastValueRef.current = html;
    onChange?.(html);
  };

  const addLink = () => {
    const url = window.prompt("URL du lien (https://...) :");
    if (!url) return;
    exec("createLink", url);
  };

  const isEmpty = !value || value.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <div className="border border-slate-300 rounded-md overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-slate-200 bg-slate-50">
        <ToolBtn onClick={() => exec("bold")} title="Gras"><Bold className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Italique"><Italic className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("underline")} title="Souligné"><Underline className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec("formatBlock", "<h3>")} title="Titre"><Heading2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("insertUnorderedList")} title="Liste à puces"><List className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("insertOrderedList")} title="Liste numérotée"><ListOrdered className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={addLink} title="Lien"><Link2 className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("removeFormat")} title="Effacer la mise en forme"><Eraser className="w-4 h-4" /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec("undo")} title="Annuler"><Undo className="w-4 h-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("redo")} title="Refaire"><Redo className="w-4 h-4" /></ToolBtn>
      </div>
      <div className="relative">
        {isEmpty && !isFocused && (
          <div className="absolute inset-0 pointer-events-none px-3 py-2 text-sm text-slate-400">
            {placeholder || "Saisir un commentaire (optionnel)…"}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={() => { setIsFocused(false); emit(); }}
          onFocus={() => setIsFocused(true)}
          className="rich-content min-h-[110px] px-3 py-2 focus:outline-none"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      </div>
    </div>
  );
}

function ToolBtn({ children, onClick, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="p-1.5 rounded hover:bg-slate-200 text-slate-700"
      title={title}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="w-px h-5 bg-slate-300 mx-1" />;
}
